import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { FileText, Plus, Trash2, Upload } from 'lucide-react';
import {
  formatCents,
  formatDate,
  leaseCreateSchema,
  MAX_FILE_BYTES,
  parseEuroToCents,
  todayIso,
  type LeaseCreate,
} from '@vuokramani/shared';
import { DetailList, pageStyles as s } from '../../components/page.tsx';
import {
  Alert,
  Button,
  ChoiceGroup,
  Notice,
  Panel,
  SelectField,
  TextArea,
  TextField,
} from '../../components/ui.tsx';
import { ApiError, errorKey } from '../../lib/api.ts';
import { useCreateLease, useUploadFiles } from '../../lib/tenancy.ts';
import { useCurrentProperty } from './PropertyLayout.tsx';
import ts from '../shared/tenancy.module.css';

interface TenantRow {
  name: string;
  email: string;
  invite: boolean;
}

interface FormState {
  startDate: string;
  fixedTerm: boolean;
  endDate: string;
  rent: string;
  dueDay: string;
  deposit: string;
  waterBilling: LeaseCreate['waterBilling'];
  water: string;
  electricityBilling: LeaseCreate['electricityBilling'];
  rentIncreaseTerms: string;
  petsAllowed: boolean;
  tenants: TenantRow[];
  signedOn: string;
  /** auto = päätellään alkupäivästä: yli kuukausi sitten alkanut → tehty muualla. */
  moveIn: 'auto' | 'app' | 'external';
}

const INITIAL: FormState = {
  startDate: '',
  fixedTerm: false,
  endDate: '',
  rent: '',
  dueDay: '5',
  deposit: '',
  waterBilling: 'per_person',
  water: '',
  electricityBilling: 'own_contract',
  rentIncreaseTerms: '',
  petsAllowed: false,
  tenants: [{ name: '', email: '', invite: true }],
  signedOn: '',
  moveIn: 'auto',
};

/** Yli kuukausi sitten alkaneessa vuokrasuhteessa muuttotarkastus on yleensä jo tehty. */
function defaultMoveIn(startDate: string): 'app' | 'external' {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) return 'app';
  const monthAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  return startDate < monthAgo ? 'external' : 'app';
}

const STEPS = ['terms', 'tenants', 'signing'] as const;
type Errors = Record<string, string>;

/** Rahakenttä euroina → sentit; tyhjä = null, virheellinen = NaN. */
function cents(value: string): number | null {
  if (value.trim() === '') return null;
  return parseEuroToCents(value) ?? Number.NaN;
}

/**
 * Olemassa olevan vuokrasuhteen kirjaus kolmessa vaiheessa: ehdot, vuokralaiset ja
 * allekirjoitus. Sopimus on jo allekirjoitettu paperilla, joten vuokrasuhde on heti voimassa.
 * Vuokralaisen kutsu sovellukseen on valinnainen.
 */
export function NewLeasePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const p = useCurrentProperty();
  const create = useCreateLease(p.id);
  const upload = useUploadFiles(p.id);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [contract, setContract] = useState<File | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const base = `/vuokranantaja/asunnot/${p.id}/vuokrasuhde`;

  const set =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) =>
      setForm((f) => ({ ...f, [key]: value }));
  const setTenant = (index: number, patch: Partial<TenantRow>) =>
    setForm((f) => ({
      ...f,
      tenants: f.tenants.map((x, i) => (i === index ? { ...x, ...patch } : x)),
    }));
  const err = (key: string) => (errors[key] ? t(errors[key]) : undefined);

  /** Koko lomake skeemalle; virheet kenttien nimillä (vuokralaiset: tenants.0.name). */
  function build() {
    const rent = cents(form.rent);
    const deposit = cents(form.deposit);
    const water = cents(form.water);
    const money: Errors = {};
    if (rent === null || Number.isNaN(rent)) money.rent = 'errors.amount';
    if (deposit !== null && Number.isNaN(deposit)) money.deposit = 'errors.amount';
    if (form.waterBilling === 'per_person' && (water === null || Number.isNaN(water))) {
      money.water = 'errors.amount';
    }
    const parsed = leaseCreateSchema.safeParse({
      startDate: form.startDate,
      endDate: form.fixedTerm ? form.endDate || '' : null,
      rentCents: rent ?? 0,
      dueDay: Number(form.dueDay),
      depositCents: deposit ?? 0,
      waterBilling: form.waterBilling,
      waterCents: form.waterBilling === 'per_person' ? (water ?? null) : null,
      electricityBilling: form.electricityBilling,
      rentIncreaseTerms: form.rentIncreaseTerms,
      petsAllowed: form.petsAllowed,
      moveInInspection: form.moveIn === 'auto' ? defaultMoveIn(form.startDate) : form.moveIn,
      signedOn: form.signedOn,
      tenants: form.tenants,
    });
    const all: Errors = { ...money };
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const key = issue.path.join('.');
        const field =
          key === 'rentCents'
            ? 'rent'
            : key === 'depositCents'
              ? 'deposit'
              : key === 'waterCents'
                ? 'water'
                : key;
        all[field] ??= issue.message.startsWith('errors.') ? issue.message : 'errors.required';
      }
    }
    return { parsed, errors: all };
  }

  const STEP_FIELDS: Array<(k: string) => boolean> = [
    (k) => !k.startsWith('tenants') && k !== 'signedOn',
    (k) => k.startsWith('tenants'),
    () => true,
  ];

  function next(event: FormEvent) {
    event.preventDefault();
    const { errors: all } = build();
    const mine = Object.fromEntries(Object.entries(all).filter(([k]) => STEP_FIELDS[step]!(k)));
    setErrors(mine);
    if (Object.keys(mine).length === 0) setStep((n) => n + 1);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const { parsed, errors: all } = build();
    setErrors(all);
    if (!parsed.success || Object.keys(all).length > 0) return;
    const lease = await create.mutateAsync(parsed.data).catch(() => null);
    if (!lease) return;
    if (contract) {
      // Sopimus Sopimukset-kansioon, näkyy vuokralaiselle.
      await upload
        .mutateAsync({ files: [contract], folder: 'contract', shared: true })
        .catch(() => undefined);
    }
    void navigate(base, { replace: true });
  }

  const money = (v: string) => {
    const c = cents(v);
    return c === null || Number.isNaN(c) ? '–' : formatCents(c, i18n.language);
  };
  const createError =
    create.error instanceof ApiError && create.error.code === 'has_active_lease'
      ? t('newLease.hasActive')
      : create.isError
        ? t(errorKey(create.error))
        : undefined;

  return (
    <div className={s.wizard}>
      <ol
        className={s.steps}
        aria-label={t('newProperty.stepOf', { current: step + 1, total: STEPS.length })}
      >
        {STEPS.map((key, index) => (
          <li
            key={key}
            className={`${s.step} ${index === step ? s.stepCurrent : ''} ${index < step ? s.stepDone : ''}`}
            aria-current={index === step ? 'step' : undefined}
          >
            <span className={s.stepNumber}>{index + 1}</span>
            <span>{t(`newLease.steps.${key}`)}</span>
          </li>
        ))}
      </ol>

      <Panel>
        {step === 0 && (
          <form className={s.form} onSubmit={next} noValidate>
            <h2 className={s.sectionTitle}>{t('newLease.termsTitle')}</h2>
            <div className={s.formRow}>
              <TextField
                label={t('newLease.startDate')}
                type="date"
                value={form.startDate}
                onChange={(e) => set('startDate')(e.target.value)}
                error={err('startDate')}
              />
              <SelectField
                label={t('lease.duration')}
                value={form.fixedTerm ? 'fixed' : 'indefinite'}
                onChange={(e) => set('fixedTerm')(e.target.value === 'fixed')}
              >
                <option value="indefinite">{t('lease.indefinite')}</option>
                <option value="fixed">{t('newLease.fixedTerm')}</option>
              </SelectField>
            </div>
            {form.fixedTerm && (
              <TextField
                label={t('newLease.endDate')}
                type="date"
                value={form.endDate}
                onChange={(e) => set('endDate')(e.target.value)}
                error={err('endDate')}
                style={{ maxWidth: 220 }}
              />
            )}
            <div className={s.formRow}>
              <TextField
                label={t('newLease.rent')}
                inputMode="decimal"
                value={form.rent}
                onChange={(e) => set('rent')(e.target.value)}
                error={err('rent')}
              />
              <SelectField
                label={t('lease.dueDay')}
                value={form.dueDay}
                onChange={(e) => set('dueDay')(e.target.value)}
              >
                {Array.from({ length: 31 }, (_, i) => String(i + 1)).map((d) => (
                  <option key={d} value={d}>
                    {t('lease.dueDayValue', { day: d })}
                  </option>
                ))}
              </SelectField>
            </div>
            <TextField
              label={t('newLease.deposit')}
              hint={t('newLease.depositHint')}
              inputMode="decimal"
              value={form.deposit}
              onChange={(e) => set('deposit')(e.target.value)}
              error={err('deposit')}
              style={{ maxWidth: 220 }}
            />
            <div className={s.formRow}>
              <SelectField
                label={t('lease.waterLabel')}
                value={form.waterBilling}
                onChange={(e) => set('waterBilling')(e.target.value as FormState['waterBilling'])}
              >
                <option value="per_person">{t('lease.water.per_person')}</option>
                <option value="metered">{t('lease.water.metered')}</option>
                <option value="included">{t('lease.water.included')}</option>
              </SelectField>
              {form.waterBilling === 'per_person' && (
                <TextField
                  label={t('newLease.waterPerPerson')}
                  inputMode="decimal"
                  value={form.water}
                  onChange={(e) => set('water')(e.target.value)}
                  error={err('water')}
                />
              )}
            </div>
            <SelectField
              label={t('lease.electricityLabel')}
              value={form.electricityBilling}
              onChange={(e) =>
                set('electricityBilling')(e.target.value as FormState['electricityBilling'])
              }
            >
              <option value="own_contract">{t('lease.electricity.own_contract')}</option>
              <option value="included">{t('lease.electricity.included')}</option>
            </SelectField>
            <TextArea
              label={t('newLease.increase')}
              hint={t('newLease.increaseHint')}
              rows={2}
              value={form.rentIncreaseTerms}
              onChange={(e) => set('rentIncreaseTerms')(e.target.value)}
            />
            <label className={ts.check}>
              <input
                type="checkbox"
                checked={form.petsAllowed}
                onChange={(e) => set('petsAllowed')(e.target.checked)}
              />
              <span>{t('occupants.petsAllowed')}</span>
            </label>
            <div className={s.formActions}>
              <Link to={base} style={{ alignSelf: 'center' }}>
                {t('common.cancel')}
              </Link>
              <Button type="submit" variant="primary">
                {t('common.next')}
              </Button>
            </div>
          </form>
        )}

        {step === 1 && (
          <form className={s.form} onSubmit={next} noValidate>
            <h2 className={s.sectionTitle}>{t('newLease.tenantsTitle')}</h2>
            <Notice>{t('newLease.tenantsHelp')}</Notice>
            {form.tenants.map((tenant, i) => (
              <fieldset key={i} className={ts.tenantBox}>
                <legend className="visually-hidden">{t('newLease.tenantN', { n: i + 1 })}</legend>
                <div className={s.formRow}>
                  <TextField
                    label={t('newLease.tenantName')}
                    value={tenant.name}
                    onChange={(e) => setTenant(i, { name: e.target.value })}
                    error={err(`tenants.${i}.name`)}
                  />
                  <TextField
                    label={t('newLease.tenantEmail')}
                    hint={tenant.invite ? undefined : t('newLease.emailOptional')}
                    type="email"
                    value={tenant.email}
                    onChange={(e) => setTenant(i, { email: e.target.value })}
                    error={err(`tenants.${i}.email`)}
                  />
                </div>
                <div className={ts.tenantFoot}>
                  <label className={ts.check}>
                    <input
                      type="checkbox"
                      checked={tenant.invite}
                      onChange={(e) => setTenant(i, { invite: e.target.checked })}
                    />
                    <span>{t('newLease.invite')}</span>
                  </label>
                  {form.tenants.length > 1 && (
                    <Button
                      variant="ghost"
                      onClick={() =>
                        setForm((f) => ({ ...f, tenants: f.tenants.filter((_, j) => j !== i) }))
                      }
                    >
                      <Trash2 size={15} aria-hidden="true" />
                      {t('common.delete')}
                    </Button>
                  )}
                </div>
                <p className={ts.fileMeta}>
                  {tenant.invite ? t('newLease.inviteOn') : t('newLease.inviteOff')}
                </p>
              </fieldset>
            ))}
            {form.tenants.length < 6 && (
              <Button
                variant="ghost"
                style={{ justifySelf: 'start' }}
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    tenants: [...f.tenants, { name: '', email: '', invite: true }],
                  }))
                }
              >
                <Plus size={16} aria-hidden="true" />
                {t('newLease.addTenant')}
              </Button>
            )}
            <div className={s.formActions}>
              <Button variant="ghost" onClick={() => setStep(0)}>
                {t('common.back')}
              </Button>
              <Button type="submit" variant="primary">
                {t('common.next')}
              </Button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form className={s.form} onSubmit={submit} noValidate>
            <h2 className={s.sectionTitle}>{t('newLease.signingTitle')}</h2>
            <ChoiceGroup<'paper' | 'bank_id'>
              name="signing"
              legend={t('newLease.signingHow')}
              value="paper"
              onChange={() => undefined}
              options={[
                {
                  value: 'paper',
                  title: t('newLease.paper'),
                  text: t('newLease.paperText'),
                },
                {
                  value: 'bank_id',
                  title: t('newLease.bankId'),
                  text: t('newLease.bankIdSoon'),
                  disabled: true,
                },
              ]}
            />
            <TextField
              label={t('newLease.signedOn')}
              type="date"
              max={todayIso()}
              value={form.signedOn}
              onChange={(e) => set('signedOn')(e.target.value)}
              error={err('signedOn')}
              style={{ maxWidth: 220 }}
            />
            <ChoiceGroup<'app' | 'external'>
              name="moveIn"
              legend={t('newLease.moveIn')}
              value={form.moveIn === 'auto' ? defaultMoveIn(form.startDate) : form.moveIn}
              onChange={(v) => set('moveIn')(v)}
              options={[
                { value: 'app', title: t('newLease.moveInApp'), text: t('newLease.moveInAppText') },
                {
                  value: 'external',
                  title: t('newLease.moveInExternal'),
                  text: t('newLease.moveInExternalText'),
                },
              ]}
            />
            <div>
              <span className={ts.groupTitle} style={{ fontSize: 'var(--text-md)' }}>
                {t('newLease.contractFile')}
              </span>
              <label className={ts.dropzone}>
                {contract ? (
                  <FileText size={20} aria-hidden="true" />
                ) : (
                  <Upload size={20} aria-hidden="true" />
                )}
                <span>{contract ? contract.name : t('newLease.chooseContract')}</span>
                <span className={ts.fileMeta}>{t('newLease.contractHint')}</span>
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  className="visually-hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    setContract(file && file.size <= MAX_FILE_BYTES ? file : null);
                  }}
                />
              </label>
            </div>

            <div>
              <h3 className={ts.groupTitle} style={{ fontSize: 'var(--text-md)' }}>
                {t('newLease.summary')}
              </h3>
              <DetailList
                rows={[
                  {
                    label: t('lease.rent'),
                    value: t('lease.perMonth', { amount: money(form.rent) }),
                  },
                  {
                    label: t('lease.start'),
                    value: form.startDate ? formatDate(form.startDate, i18n.language) : '–',
                  },
                  {
                    label: t('lease.tenants', { count: form.tenants.length }),
                    value: form.tenants
                      .map((x) => (x.invite ? `${x.name} (${t('newLease.willInvite')})` : x.name))
                      .join(', '),
                  },
                ]}
              />
            </div>

            {createError && <Alert>{createError}</Alert>}
            <div className={s.formActions}>
              <Button variant="ghost" onClick={() => setStep(1)}>
                {t('common.back')}
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={create.isPending || upload.isPending}
              >
                {t('newLease.save')}
              </Button>
            </div>
          </form>
        )}
      </Panel>
    </div>
  );
}
